import { Cell, Grid, Heading, RoadMap, headingBetween } from "@highmarch/domain";
import { BridgeFit, deckHeightAt } from "./bridgeFit";

/** One run of chasm cells the road crosses, the heading it crosses along and its level deck guess (bank height). */
export interface BridgeSpan { readonly cells: Cell[]; readonly heading: Heading; readonly deck: number }

/**
 * The bridges on the current road (Terrain holds one): which cells they cover and how high their deck is there.
 *
 * A run of consecutive chasm cells along the road is ONE span, which is why the kit ships `g_bridge_1/2/3` — three
 * chasm cells in a row want a single bridge, not three one-cell bridges butted together. Until the road view has
 * fitted a span to the gorge as drawn (bridgeFit.ts, `setFits`), its deck is level at the bank's height.
 */
export class BridgeSpans {
  private readonly decks = new Map<number, number>();
  readonly spans: BridgeSpan[] = [];
  private fits: readonly (BridgeFit | null)[] = [];

  /** `gap` says which cells are chasm or lava; `ground` is the levelled ground height (Terrain.hillAt). */
  constructor(private readonly grid: Grid, private readonly gap: (c: Cell) => boolean, private readonly ground: (x: number, z: number) => number) {}

  /** Re-reads the road so bridges follow the draft. */
  setRoad(road: RoadMap): void {
    this.decks.clear();
    this.spans.length = 0;
    this.fits = [];
    const cells = road.roadCells;
    let i = 0;
    while (i < cells.length) {
      if (!this.gap(cells[i])) { i++; continue; }
      let j = i;
      while (j + 1 < cells.length && this.gap(cells[j + 1])) j++;
      const prev = i > 0 ? cells[i - 1] : null, next = j + 1 < cells.length ? cells[j + 1] : null;
      const run = cells.slice(i, j + 1);
      // A run at the very end of the road — the portal landing on a chasm — has only one bank: it takes that bank's
      // height. Without this the last cells of the road drop to the floor of the gorge.
      const bank = prev ?? next;
      if (!bank) { i = j + 1; continue; }                              // a road made only of chasm: nothing to span from
      const other = prev && next ? next : bank;
      const deck = Math.max(this.ground(bank.x, bank.y), this.ground(other.x, other.y)) + BridgeSpans.Lift;
      for (const c of run) this.decks.set(this.grid.index(c), deck);
      // headingBetween needs two ADJACENT cells, and the banks either side of a one-cell gorge are two apart. Take
      // the direction from the bank into the run instead — the road enters the crossing that way.
      const heading = prev ? headingBetween(prev, run[0]) : headingBetween(run[run.length - 1], next!);
      this.spans.push({ cells: run, heading, deck });
      i = j + 1;
    }
  }

  /** How far the level guess sits above the bank. */
  static readonly Lift = 0.06;

  /** The decks as fitted to the drawn gorge, one per span (or null where a span could not be fitted). */
  setFits(fits: readonly (BridgeFit | null)[]): void { this.fits = fits; }

  /** The level deck guess over a bridged cell (by grid index), or undefined where no bridge is. */
  levelAt(i: number): number | undefined { return this.decks.get(i); }

  /** Deck height of whichever fitted deck covers a world point — over the gorge or on its seat on a bank — else NaN. */
  deckOver(x: number, z: number): number {
    for (const f of this.fits) {
      const y = f ? deckHeightAt(f, x, z) : NaN;
      if (!Number.isNaN(y)) return y;
    }
    return NaN;
  }

  /** Deck height at a world point over bridged cell `i`: the fitted deck's line through it, else the level guess. */
  deckAt(i: number, x: number, z: number): number | undefined {
    const flat = this.decks.get(i);
    if (flat === undefined) return undefined;
    const y = this.deckOver(x, z);
    return Number.isNaN(y) ? flat : y;
  }
}
